import { type ReactNode } from 'react'
import cx from 'classnames'
import styles from './EmptyState.module.css'
import { Icon, type IconName } from '@/components/Icon'

export interface EmptyStateProps {
  children?: ReactNode
  size?: 'base' | 'lg' | 'dialog'

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * The empty state root.
 */
export function EmptyState({ size, children, testId }: EmptyStateProps) {
  return (
    <div data-test={testId} className={cx(styles.root)}>
      <div
        className={cx(
          styles.content,
          size === 'lg' && styles.lg,
          size === 'dialog' && styles.dialog,
        )}
      >
        {children}
      </div>
    </div>
  )
}

interface EmptyStateTitleProps {
  children?: ReactNode
  testId?: string
}

/**
 * Empty State Title
 */
export function EmptyStateTitle({ children, testId }: EmptyStateTitleProps) {
  return (
    <h3 className={styles.title} data-test={testId}>
      {children}
    </h3>
  )
}

interface EmptyStateDescriptionProps {
  children?: ReactNode
  testId?: string
}

/**
 * Empty State Description
 */
export function EmptyStateDescription({
  children,
  testId,
}: EmptyStateDescriptionProps) {
  return (
    <div className={styles.description} data-test={testId}>
      {children}
    </div>
  )
}

interface EmptyStateActionsProps {
  children?: ReactNode
  testId?: string
}

/**
 * Empty State Actions
 */
export function EmptyStateActions({
  children,
  testId,
}: EmptyStateActionsProps) {
  return (
    <div data-test={testId} className={styles.actions}>
      {children}
    </div>
  )
}

interface EmptyStateDecorationProps {
  children?: ReactNode
}

/**
 * Empty State Decoration
 */
export function EmptyStateDecoration({ children }: EmptyStateDecorationProps) {
  return <div className="flex flex-col items-center mb-2xl">{children}</div>
}

interface EmptyStateIconDecorationProps {
  icon: IconName
}

/**
 * Empty State Icon Decoration
 */
export function EmptyStateIconDecoration({
  icon,
}: EmptyStateIconDecorationProps) {
  return (
    <EmptyStateDecoration>
      <Icon name={icon} variant="surface" />
    </EmptyStateDecoration>
  )
}
